import {
  FieldMapArrayItem,
  FieldMapArrayPayload,
  MapObjectsProps,
  MapObjectsRef,
  Mapping,
  ObjectConfig,
  ObjectConfigPayload,
  ValidationErrors
} from "./types/mapObjects";
import {
  AfterGroupByTransformationExampleJSON,
  AfterMapObjectTransformationExampleRows
} from "./constants/transformationInputOutput";
import { DestinationSelectInputField } from "@/components/custom/InputField/WorkflowSelectInput";
import { NestedDropdown } from "@/components/custom/MultiLayeredDropdownField";
import { Button } from "@/components/ui/button";
import { FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ButtonVariant } from "@/constants/button/button";
import { INPUT_FIELD_CLASSNAME } from "@/constants/classname/classname";
import { AppFieldsType, CampArrayFieldOptions } from "@/constants/workflows/workflowCreate";
import { getAppFieldsAccordingToType } from "@/helpers/appFields";
import { RootState } from "@/redux";
import { cn } from "@/lib/utils";
import { Plus, Trash2 } from "lucide-react";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { InputFieldLabel } from "@/components/custom/InputField/Label"

export const MapObjects = forwardRef<MapObjectsRef, MapObjectsProps>(({
  allFields,
  initialConfiguration,
}, ref) => {
  const { applications } = useSelector((state: RootState) => state.application);
  const [objectConfigs, setObjectConfigs] = useState<ObjectConfig[]>(() => {
    const arrayConfigs: ObjectConfigPayload[] = Array.isArray(initialConfiguration)
      ? initialConfiguration
      : initialConfiguration?.arrayConfigs ?? [];

    if (arrayConfigs.length > 0) {
      return arrayConfigs.map((config, idx) => {
        const mapObjectName = config.mapObjectName || config.name || "";
        const fieldMapArrays: FieldMapArrayPayload[] = config.fieldMapArray && config.fieldMapArray.length > 0
          ? config.fieldMapArray
          : config.mapArray && config.mapArray.length > 0
            ? [{ mappings: config.mapArray }]
            : [];

        const normalizedFieldMapArray: FieldMapArrayItem[] = fieldMapArrays.length > 0
          ? fieldMapArrays.map((fma, fmaIdx) => {
            const rawMappings = fma.mappings && fma.mappings.length > 0
              ? fma.mappings
              : fma.mapArray && fma.mapArray.length > 0
                ? fma.mapArray
                : [];

            const mappings: Mapping[] = rawMappings.length > 0
              ? rawMappings.map((m) => {
                const fromValue = m.from;
                const toValue = m.to;
                return {
                  from: fromValue,
                  fromLabel: fromValue,
                  to: toValue,
                };
              })
              : [{ from: "", fromLabel: "", to: "" }];

            return {
              id: `fieldmap-${Date.now()}-${fmaIdx}`,
              mappings,
            };
          })
          : [{
            id: `fieldmap-${Date.now()}-${idx}`,
            mappings: [{ from: "", fromLabel: "", to: "" }]
          }];

        return {
          id: `object-${Date.now()}-${idx}`,
          mapObjectName,
          fieldMapArray: normalizedFieldMapArray,
        };
      });
    }

    return [
      {
        id: `object-${Date.now()}`,
        mapObjectName: "",
        fieldMapArray: [
          {
            id: `fieldmap-${Date.now()}`,
            mappings: [
              {
                from: "",
                fromLabel: "",
                to: "",
              },
            ],
          },
        ],
      },
    ];
  });
  const [errors, setErrors] = useState<ValidationErrors>({
    objectConfigs: [{
      mapObjectName: false,
      fieldMapArray: [{
        mappings: [{ from: false, to: false }]
      }]
    }]
  });

  const buildTemplateMappings = (templates: Mapping[]) => {
    if (!templates.length) {
      return [{ from: "", fromLabel: "", to: "" }];
    }
    return templates.map(mapping => ({
      from: "",
      fromLabel: "",
      to: mapping.to ?? ""
    }));
  };

  const buildTemplateErrorMappings = (count: number) => {
    if (!count) {
      return [{ from: false, to: false }];
    }
    return Array.from({ length: count }, () => ({ from: false, to: false }));
  };

  const buildUnionTemplateMappings = (fieldMapArray: FieldMapArrayItem[]) => {
    if (!fieldMapArray.length) return [];
    const seen = new Set<string>();
    const template: Mapping[] = [];
    fieldMapArray.forEach(item => {
      item.mappings.forEach(mapping => {
        const destination = (mapping.to ?? "").trim();
        if (!destination) return;
        if (seen.has(destination)) return;
        seen.add(destination);
        template.push({
          from: "",
          fromLabel: "",
          to: mapping.to ?? ""
        });
      });
    });
    return template;
  };

  useImperativeHandle(ref, () => ({
    validate: () => {
      const newErrors: ValidationErrors = {
        objectConfigs: []
      };

      // Validate each object configuration
      objectConfigs.forEach((config) => {
        const configError = {
          mapObjectName: !config.mapObjectName.trim(),
          fieldMapArray: [] as Array<{
            mappings: Array<{ from: boolean; to: boolean }>;
          }>
        };

        // Validate each fieldMapArray item
        config.fieldMapArray.forEach((fieldMapItem) => {
          const fieldMapError = {
            mappings: [] as Array<{ from: boolean; to: boolean }>
          };

          // Validate each mapping
          fieldMapItem.mappings.forEach((mapping) => {
            fieldMapError.mappings.push({
              from: !mapping.from.trim(),
              to: !mapping.to.trim()
            });
          });

          configError.fieldMapArray.push(fieldMapError);
        });

        newErrors.objectConfigs.push(configError);
      });

      setErrors(newErrors);

      // Return true if no errors
      const hasErrors = newErrors.objectConfigs.some(
        config => config.mapObjectName ||
          config.fieldMapArray.some(fma => fma.mappings.some(m => m.from || m.to))
      );

      return !hasErrors;
    },
    getData: () => {
      const arrayConfigs = objectConfigs.reduce<Array<{
        mapObjectName: string;
        fieldMapArray: Array<{ mappings: Array<{ from: string; to: string }> }>;
      }>>((acc, config) => {
        if (!config.mapObjectName.trim()) {
          return acc;
        }

        const fieldMapArray = config.fieldMapArray.reduce<Array<{ mappings: Array<{ from: string; to: string }> }>>((innerAcc, fieldMapItem) => {
          const mappings = fieldMapItem.mappings
            .filter(mapping => mapping.from.trim() && mapping.to.trim())
            .map(mapping => ({
              from: mapping.from.trim(),
              to: mapping.to.trim()
            }));

          if (mappings.length > 0) {
            innerAcc.push({ mappings });
          }

          return innerAcc;
        }, []);

        if (fieldMapArray.length > 0) {
          acc.push({
            mapObjectName: config.mapObjectName,
            fieldMapArray
          });
        }

        return acc;
      }, []);

      return {
        arrayConfigs
      };
    }
  }));

  const responseFieldTree = useMemo(
    () => getAppFieldsAccordingToType(allFields, AppFieldsType.RESPONSE_TYPE, applications),
    [allFields, applications]
  );

  return (
    <div className="text-primary flex flex-col max-h-[500px] min-h-[300px] h-full">
      <div className="flex-1 overflow-y-auto thin-scrollbar pr-2">
        <div className="flex w-full mb-4">
          <div className="w-1/2 flex-col items-start">
            <p className="text-sm font-medium">
              Example original rows
            </p>
            <div className="bg-foreground rounded-md border p-4 mt-1 max-h-[250px] overflow-auto thin-scrollbar">
              <pre className="text-xs">
                {JSON.stringify(AfterGroupByTransformationExampleJSON, null, 2)}
              </pre>
            </div>
          </div>
          <div className="mx-1" />
          <div className="w-1/2">
            <p className="text-sm font-medium">Transformed rows after mapping object fields</p>
            <div className="bg-foreground rounded-md border p-4 mt-1 max-h-[250px] overflow-auto thin-scrollbar">
              <pre className="text-xs">
                {JSON.stringify(AfterMapObjectTransformationExampleRows, null, 2)}
              </pre>
            </div>
          </div>
        </div>
        <div className="space-y-6">
          {objectConfigs.map((config, configIndex) => (
            <div key={config.id} className={cn(
              "rounded-md border p-4 shadow-sm",
              errors.objectConfigs[configIndex]?.mapObjectName ? "border-destructive" : ""
            )}>
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-1 max-w-[280px]">
                  <DestinationSelectInputField
                    label="Object field to Map"
                    labelDescription="e.g., parents, campers"
                    value={config.mapObjectName}
                    selectOptions={CampArrayFieldOptions}
                    onChange={(value) => {
                      setObjectConfigs((prev) =>
                        prev.map((item, idx) =>
                          idx === configIndex ? { ...item, mapObjectName: value } : item
                        )
                      );
                      // Clear error when selecting
                      setErrors(prev => ({
                        ...prev,
                        objectConfigs: prev.objectConfigs.map((err, idx) =>
                          idx === configIndex ? { ...err, mapObjectName: false } : err
                        )
                      }));
                    }}
                    additionalClassName="bg-foreground"
                    hasError={errors.objectConfigs[configIndex]?.mapObjectName}
                  />
                  {errors.objectConfigs[configIndex]?.mapObjectName && (
                    <p className="text-destructive text-xs mt-1">Object field name is required</p>
                  )}
                </div>
                {objectConfigs.length > 1 && (
                  <Button
                    variant={ButtonVariant.ICON}
                    className="ml-auto"
                    onClick={() => {
                      setObjectConfigs((prev) => prev.filter((_, idx) => idx !== configIndex));
                      setErrors(prev => ({
                        ...prev,
                        objectConfigs: prev.objectConfigs.filter((_, idx) => idx !== configIndex)
                      }));
                    }}
                  >
                    <Trash2 className="text-destructive" size={16} />
                  </Button>
                )}
              </div>

              {config.mapObjectName && (
                <>
                  <div className="mt-4">
                    <InputFieldLabel
                      label="Field Mapping Sets"
                      description="Create multiple mapping sets for different records (e.g., parent1, parent2, or camper1, camper2, camper3)"
                    />

                    {config.fieldMapArray.map((fieldMapItem, fieldMapIndex) => (
                      <div key={fieldMapItem.id} className="mb-4 rounded-md border border-accent bg-accent-background p-4">
                        <div className="flex items-center justify-between mb-3">
                          <FormLabel className="text-sm font-medium text-primary">
                            Mapping Set {fieldMapIndex + 1}
                          </FormLabel>
                          {config.fieldMapArray.length > 1 && (
                            <Button
                              variant={ButtonVariant.ICON}
                              onClick={() => {
                                setObjectConfigs((prev) =>
                                  prev.map((objConfig, objIdx) => {
                                    if (objIdx !== configIndex) return objConfig;
                                    return {
                                      ...objConfig,
                                      fieldMapArray: objConfig.fieldMapArray.filter((_, fmIdx) => fmIdx !== fieldMapIndex)
                                    };
                                  })
                                );
                                // Update errors
                                setErrors(prev => ({
                                  ...prev,
                                  objectConfigs: prev.objectConfigs.map((err, idx) =>
                                    idx === configIndex
                                      ? {
                                        ...err,
                                        fieldMapArray: err.fieldMapArray.filter((_, fmIdx) => fmIdx !== fieldMapIndex)
                                      }
                                      : err
                                  )
                                }));
                              }}
                            >
                              <Trash2 className="text-destructive" size={16} />
                            </Button>
                          )}
                        </div>

                        {fieldMapItem.mappings.map((mapping, mappingIndex) => (
                          <div key={`${fieldMapItem.id}-mapping-${mappingIndex}`} className="flex flex-wrap items-start gap-4 mb-3">
                            <div className="flex-1 min-w-[240px] max-w-[280px]">
                              <NestedDropdown
                                data={responseFieldTree}
                                field={mapping.from}
                                label="From field"
                                description="Select the source field"
                                initialSelectedLabel={mapping.fromLabel || mapping.from}
                                onSelect={(item) => {
                                  setObjectConfigs((prev) =>
                                    prev.map((objConfig, objIdx) => {
                                      if (objIdx !== configIndex) return objConfig;
                                      return {
                                        ...objConfig,
                                        fieldMapArray: objConfig.fieldMapArray.map((fmItem, fmIdx) => {
                                          if (fmIdx !== fieldMapIndex) return fmItem;
                                          return {
                                            ...fmItem,
                                            mappings: fmItem.mappings.map((map, mapIdx) =>
                                              mapIdx === mappingIndex
                                                ? { ...map, from: item.codeChain, fromLabel: item.displayName }
                                                : map
                                            )
                                          };
                                        })
                                      };
                                    })
                                  );
                                  // Clear error
                                  setErrors(prev => ({
                                    ...prev,
                                    objectConfigs: prev.objectConfigs.map((err, idx) =>
                                      idx === configIndex
                                        ? {
                                          ...err,
                                          fieldMapArray: err.fieldMapArray.map((fmErr, fmIdx) =>
                                            fmIdx === fieldMapIndex
                                              ? {
                                                ...fmErr,
                                                mappings: fmErr.mappings.map((mErr, mIdx) =>
                                                  mIdx === mappingIndex ? { ...mErr, from: false } : mErr
                                                )
                                              }
                                              : fmErr
                                          )
                                        }
                                        : err
                                    )
                                  }));
                                }}
                                showErrorFields={errors.objectConfigs[configIndex]?.fieldMapArray[fieldMapIndex]?.mappings[mappingIndex]?.from}
                              />
                              {errors.objectConfigs[configIndex]?.fieldMapArray[fieldMapIndex]?.mappings[mappingIndex]?.from && (
                                <p className="text-destructive text-xs">From field cannot be empty</p>
                              )}
                            </div>
                            <div className="flex-1 min-w-[240px] max-w-[280px]">
                              <InputFieldLabel
                                label="To field"
                                description="New field name for the mapped value"
                              />
                              <Input
                                className={cn(
                                  INPUT_FIELD_CLASSNAME,
                                  "bg-foreground",
                                  errors.objectConfigs[configIndex]?.fieldMapArray[fieldMapIndex]?.mappings[mappingIndex]?.to && "border-destructive bg-destructive-foreground"
                                )}
                                type="text"
                                value={mapping.to}
                                placeholder="e.g., leadName"
                                onChange={(event) => {
                                  const { value } = event.target;
                                  setObjectConfigs((prev) =>
                                    prev.map((objConfig, objIdx) => {
                                      if (objIdx !== configIndex) return objConfig;
                                      return {
                                        ...objConfig,
                                        fieldMapArray: objConfig.fieldMapArray.map((fmItem, fmIdx) => {
                                          if (fmIdx !== fieldMapIndex) return fmItem;
                                          return {
                                            ...fmItem,
                                            mappings: fmItem.mappings.map((map, mapIdx) =>
                                              mapIdx === mappingIndex ? { ...map, to: value } : map
                                            )
                                          };
                                        })
                                      };
                                    })
                                  );
                                  // Clear error
                                  setErrors(prev => ({
                                    ...prev,
                                    objectConfigs: prev.objectConfigs.map((err, idx) =>
                                      idx === configIndex
                                        ? {
                                          ...err,
                                          fieldMapArray: err.fieldMapArray.map((fmErr, fmIdx) =>
                                            fmIdx === fieldMapIndex
                                              ? {
                                                ...fmErr,
                                                mappings: fmErr.mappings.map((mErr, mIdx) =>
                                                  mIdx === mappingIndex ? { ...mErr, to: false } : mErr
                                                )
                                              }
                                              : fmErr
                                          )
                                        }
                                        : err
                                    )
                                  }));
                                }}
                              />
                              {errors.objectConfigs[configIndex]?.fieldMapArray[fieldMapIndex]?.mappings[mappingIndex]?.to && (
                                <p className="text-destructive text-xs">Specify new field name for the mapped value</p>
                              )}
                            </div>
                            {fieldMapItem.mappings.length > 1 && (
                              <Button
                                variant={ButtonVariant.ICON}
                                className="mt-10"
                                onClick={() => {
                                  setObjectConfigs((prev) =>
                                    prev.map((objConfig, objIdx) => {
                                      if (objIdx !== configIndex) return objConfig;
                                      return {
                                        ...objConfig,
                                        fieldMapArray: objConfig.fieldMapArray.map((fmItem, fmIdx) => {
                                          if (fmIdx !== fieldMapIndex) return fmItem;
                                          return {
                                            ...fmItem,
                                            mappings: fmItem.mappings.filter((_, mapIdx) => mapIdx !== mappingIndex)
                                          };
                                        })
                                      };
                                    })
                                  );
                                  // Update errors
                                  setErrors(prev => ({
                                    ...prev,
                                    objectConfigs: prev.objectConfigs.map((err, idx) =>
                                      idx === configIndex
                                        ? {
                                          ...err,
                                          fieldMapArray: err.fieldMapArray.map((fmErr, fmIdx) =>
                                            fmIdx === fieldMapIndex
                                              ? {
                                                ...fmErr,
                                                mappings: fmErr.mappings.filter((_, mIdx) => mIdx !== mappingIndex)
                                              }
                                              : fmErr
                                          )
                                        }
                                        : err
                                    )
                                  }));
                                }}
                              >
                                <Trash2 className="text-destructive" size={16} />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          className="font-semibold text-accent border-accent bg-background hover:text-accent hover:border-accent"
                          variant={ButtonVariant.OUTLINE}
                          onClick={() => {
                            setObjectConfigs((prev) =>
                              prev.map((objConfig, objIdx) =>
                                objIdx === configIndex
                                  ? {
                                    ...objConfig,
                                    fieldMapArray: objConfig.fieldMapArray.map((fmItem, fmIdx) =>
                                      fmIdx === fieldMapIndex
                                        ? {
                                          ...fmItem,
                                          mappings: [
                                            ...fmItem.mappings,
                                            { from: "", fromLabel: "", to: "" }
                                          ]
                                        }
                                        : fmItem
                                    )
                                  }
                                  : objConfig
                              )
                            );
                            // Add error entry
                            setErrors(prev => ({
                              ...prev,
                              objectConfigs: prev.objectConfigs.map((err, idx) =>
                                idx === configIndex
                                  ? {
                                    ...err,
                                    fieldMapArray: err.fieldMapArray.map((fmErr, fmIdx) =>
                                      fmIdx === fieldMapIndex
                                        ? {
                                          ...fmErr,
                                          mappings: [...fmErr.mappings, { from: false, to: false }]
                                        }
                                        : fmErr
                                    )
                                  }
                                  : err
                              )
                            }));
                          }}
                        >
                          <Plus className="mr-2 w-4 h-4" />
                          Add another field mapping
                        </Button>
                      </div>
                    ))}

                    <Button
                      type="button"
                      className="font-semibold text-accent border-accent bg-background hover:text-accent hover:border-accent"
                      variant={ButtonVariant.OUTLINE}
                      onClick={() => {
                        const currentConfig = objectConfigs[configIndex];
                        const unionTemplate = buildUnionTemplateMappings(currentConfig?.fieldMapArray ?? []);
                        const templateMappings = buildTemplateMappings(unionTemplate);
                        const templateErrorMappings = buildTemplateErrorMappings(templateMappings.length);
                        setObjectConfigs(prev =>
                          prev.map((objConfig, objIdx) => {
                            if (objIdx !== configIndex) return objConfig;
                            return {
                              ...objConfig,
                              fieldMapArray: [
                                ...objConfig.fieldMapArray,
                                {
                                  id: `fieldmap-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                                  mappings: templateMappings
                                }
                              ]
                            };
                          })
                        );
                        setErrors(prev => ({
                          ...prev,
                          objectConfigs: prev.objectConfigs.map((err, idx) => {
                            if (idx !== configIndex) return err;
                            return {
                              ...err,
                              fieldMapArray: [
                                ...err.fieldMapArray,
                                { mappings: templateErrorMappings }
                              ]
                            };
                          })
                        }));
                      }}
                    >
                      <Plus className="mr-2 w-4 h-4" />
                      Add another mapping set
                    </Button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <Button
          type="button"
          className="font-semibold text-accent border-accent bg-background hover:text-accent hover:border-accent"
          variant={ButtonVariant.OUTLINE}
          onClick={() => {
            setObjectConfigs((prev) => [
              ...prev,
              {
                id: `object-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                mapObjectName: "",
                fieldMapArray: [
                  {
                    id: `fieldmap-${Date.now()}`,
                    mappings: [{ from: "", fromLabel: "", to: "" }]
                  }
                ]
              }
            ]);
            // Add error entry for new object config
            setErrors(prev => ({
              ...prev,
              objectConfigs: [
                ...prev.objectConfigs,
                {
                  mapObjectName: false,
                  fieldMapArray: [{ mappings: [{ from: false, to: false }] }]
                }
              ]
            }));
          }}
        >
          <Plus className="mr-2 w-4 h-4" />
          Add another object mapping
        </Button>
      </div>
    </div>
  );
});
